import { Injectable } from "@angular/core";
import { ItemDataService } from "../item-data.service";
import { TieredChanceBase, ChanceBaseTierType, UniqueItem, FlatItemData, PricedItemWithImage } from "../../models/item-data-models";
import { trackError } from "../../app.config";
import { Filter } from "../../models/filter-models";
import { EconomyService } from "../economy.service";

const excludedItemClasses = [
  'Two Hand Swords', 'Two Hand Maces', 'Two Hand Axes',
  'One Hand Swords', 'One Hand Maces', 'One Hand Axes',
  'Staves', 'Warstaves', 'Quarterstaves', 'Sceptres',
  'Body Armours', 'Bows', 'Shields', 'Quivers',
  'Crossbows'
];

@Injectable({
  providedIn: 'root'
})
export class ChanceBasesTierService {
  private uniquesListData: UniqueItem[];
  private flatItemData: FlatItemData[];
  private classSpecificUniquesData: Record<string, string[]>;
  private twoHandWeaponBaseTypes: string[];

  private defaultTieredChanceBases!: TieredChanceBase[];

  private tieredChanceBases: Record<ChanceBaseTierType, TieredChanceBase[]> = {
    Searching: [] as TieredChanceBase[],
    Potential: [] as TieredChanceBase[],
    Class: [] as TieredChanceBase[],
    Other: [] as TieredChanceBase[],
  };

  private nonExistantChanceBases = ['Ring'];

  private tierCutoffValues: { tier: ChanceBaseTierType, value: number, displayValue: string }[] = [
    { tier: 'Searching', value: 1000000, displayValue: 'Top 3 Single-Unique' },
    { tier: 'Potential', value: 100, displayValue: 'Top 10 Highest Average Value' },
    { tier: 'Other', value: 0, displayValue: 'Other' },
  ]

  constructor(private itemDataService: ItemDataService, private economyService: EconomyService) {
    this.uniquesListData = this.itemDataService.getUniquesList();
    this.flatItemData = this.itemDataService.getFlatItemData();
    this.classSpecificUniquesData = this.itemDataService.getClassSpecificUniques();
    const twoHandWeaponItemClasses = this.itemDataService.getTwoHandedWeaponTypes().map(w => w.toString());
    this.twoHandWeaponBaseTypes = this.itemDataService.getItemData()
      .filter(item => twoHandWeaponItemClasses.includes(item.itemClass))
      .map(item => item.baseTypes.map(baseType => baseType.name)).flat();
  }

  initialize(filter: Filter) {
    this.buildDefaultTieredChanceBases();
    this.combineTieredChanceBases(filter);
  }

  buildDefaultTieredChanceBases() {
    const excludedChanceBaseItemClasses = this.itemDataService.getExcludedChanceBaseItemClasses();

    const chanceableUniqueValues = this.economyService.getUniquesData()
      .filter(unique => !this.itemDataService.getUniqueBossDrop(unique.name))
      .sort((a, b) => (b.price || 0) - (a.price || 0)); // Descending, so highest value unique is shown first

    const allUniques = chanceableUniqueValues.map(item => {
      const uniqueItem = this.uniquesListData.find(u => u.name === item.name);
      if (!uniqueItem) return null;
      const flatItem = this.itemDataService.getFlatItemDataByBaseTypeName(uniqueItem.baseType);
      if (!flatItem) return null;
      if (excludedChanceBaseItemClasses.includes(flatItem.itemClass)) return null;
      if (this.nonExistantChanceBases.includes(uniqueItem.baseType)) return null;

      return { uniqueName: uniqueItem.name, baseType: uniqueItem.baseType, price: item.price || 1, image: uniqueItem.image };
    }).filter(item => item !== null);

    // Group by base type
    const groupedByBaseType = allUniques.reduce((acc, item) => {
      acc[item.baseType] = acc[item.baseType] || [];
      acc[item.baseType].push({ uniqueName: item.uniqueName, price: item.price, image: item.image });
      return acc;
    }, {} as Record<string, { uniqueName: string, price: number, image?: string }[]>);

    // Exclude two-handed weapons, body armour, bows and shields from auto-tiering (they can still appear in Other)
    const baseTypesToExcludeDueToSize = this.flatItemData
      .filter(item => excludedItemClasses.includes(item.itemClass))
      .map(item => item.baseType.name);

    const singleUniqueBaseTypes = Object.entries(groupedByBaseType)
      .filter(([baseType, uniqueNames]) => uniqueNames.length === 1 && !baseTypesToExcludeDueToSize.includes(baseType))
      .sort((a, b) => (b[1][0].price || 0) - (a[1][0].price || 0));

    const hardCodedChanceBases = this.itemDataService.getHardCodedChanceBases().map(baseType => {
      return {
        uniques: groupedByBaseType[baseType].map(u => ({ name: u.uniqueName, price: u.price, image: u.image } as PricedItemWithImage)),
        baseType,
        tier: 'Searching' as ChanceBaseTierType
      } as TieredChanceBase;
    }).filter(item => item !== undefined);

    // Take the first 3 and put them in Searching
    const searching = singleUniqueBaseTypes
      .slice(0, 3 - hardCodedChanceBases.length)
      .map(([baseType, uniqueNames]) => {
      return {
        uniques: uniqueNames.map(u => ({ name: u.uniqueName, price: u.price, image: u.image } as PricedItemWithImage)),
        baseType,
        tier: 'Searching' as ChanceBaseTierType
      } as TieredChanceBase;
    });

    searching.push(...hardCodedChanceBases);

    // Get the average value, then put the top 10 highest average values in 'Potential'
    const orderedBaseTypes = Object.entries(groupedByBaseType)
      .filter(([baseType, uniqueNames]) => !searching.some(s => s.baseType === baseType) && !baseTypesToExcludeDueToSize.includes(baseType))
      .sort((a, b) => (b[1].reduce((acc, curr) => acc + (curr.price || 0), 0) / b[1].length) - (a[1].reduce((acc, curr) => acc + (curr.price || 0), 0) / a[1].length));

    const potential = orderedBaseTypes
      .slice(0, 10)
      .map(([baseType, uniqueNames]) => {
      return {
        uniques: uniqueNames.map(u => ({ name: u.uniqueName, price: u.price, image: u.image } as PricedItemWithImage)),
        baseType,
        tier: 'Potential' as ChanceBaseTierType
      } as TieredChanceBase;
    });

    // Put everything that's not in 'Searching' or 'Potential' in 'Other'
    const other = Object.entries(groupedByBaseType)
      .filter(([baseType, uniqueNames]) => !searching.some(s => s.baseType === baseType) && !potential.some(p => p.baseType === baseType))
      .map(([baseType, uniqueNames]) => {
        return {
          uniques: uniqueNames.map(u => ({ name: u.uniqueName, price: u.price, image: u.image } as PricedItemWithImage)),
          baseType,
          tier: 'Other' as ChanceBaseTierType
        } as TieredChanceBase;
      });

    this.defaultTieredChanceBases = [...searching, ...potential, ...other];
  }

  combineTieredChanceBases(filter: Filter) {
    const clone = structuredClone(this.defaultTieredChanceBases) as TieredChanceBase[];

    filter!.showClassSpecificUniquesForClasses.forEach(className => {
      const uniquesForClass = this.classSpecificUniquesData[className];
      if (uniquesForClass) {
        for (const unique of uniquesForClass) {
          const existingItem = clone.find(item => item.uniques.some(u => u.name === unique));
          if (existingItem && existingItem.tier === 'Other') {
            existingItem.tier = 'Class';
            existingItem.classSpecific = className;
          }
        }
      }
    });

    filter.customChanceBaseTiers.forEach(customItem => {
      const existingItem = clone.find(item => item.baseType === customItem.baseType);
      if (existingItem) {
        existingItem.originalTier = existingItem.tier;
        existingItem.tier = customItem.tier;
      } else {
        trackError(new Error('Unexpected custom chance base type: ' + customItem.baseType));
      }
    });

    this.tieredChanceBases['Searching'].length = 0;
    this.tieredChanceBases['Searching'].push(...clone.filter(i => i.tier === 'Searching'));
    this.tieredChanceBases['Potential'].length = 0;
    this.tieredChanceBases['Potential'].push(...clone.filter(i => i.tier === 'Potential'));
    this.tieredChanceBases['Class'].length = 0;
    this.tieredChanceBases['Class'].push(...clone.filter(i => i.tier === 'Class'));
    this.tieredChanceBases['Other'].length = 0;
    this.tieredChanceBases['Other'].push(...clone.filter(i => i.tier === 'Other'));
  }

  getTieredChanceBases() {
    return this.tieredChanceBases;
  }

  getTierCutoffValues() {
    return this.tierCutoffValues;
  }
}
